import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import type { SmartBundleViewModel } from 'autogen/swagger/all';
import { useAddMultipleToBasketMutation, useGetSmartBundleQuery } from 'autogen/swagger/all/hooks';
import {
    ColorBackgroundBrand as KicksColorBackgroundBrand,
    ColorTextPrimaryInverted as KicksColorTextPrimaryInverted
} from 'autogen/theming/themes/kicks/default';
import {
    ColorBackgroundPrimary as MatasColorBackgroundPrimary,
    ColorTextPrimary as MatasColorTextPrimary
} from 'autogen/theming/themes/matas/neutral';
import {
    AddedMultipleToBasketMessage,
    AddedToBasketMessage,
    AddMultipleToBasketButtonText,
    AddToBasketButtonText,
    CurrentProductSplash,
    DisabledAddToBasketButtonText,
    MultipleItems,
    SingleItem,
    TotalText
} from 'autogen/translation-keys/trans-website-product-smartbundle';
import { GeneralExceptionError } from 'autogen/translation-keys/trans-website-shared-error';
import { Button } from 'components/Button';
import { updateBasket } from 'components/basket/shared/basket';
import PriceModule from 'components/generic/PriceModule';
import { Typography } from 'components/generic/typography';
import Loader from 'components/Loader';
import HorizontalProductItem from 'components/shared/productItem/components/HorizontalProductItem/HorizontalProductItem';
import toast from 'components/Toast';
import useSegmentation from 'hooks/user/useSegmentation';
import { format } from 'modules/helpers/strings';
import { GA4Tracking } from 'modules/tracking/GA4/GA4Tracking';
import { breakpoint } from 'variables';

export type SmartBundleProps = {
    productId: number;
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.Spacing8};
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.Spacing8};

    ${breakpoint.up('1025px')} {
        gap: ${({ theme }) => theme.Spacing16};
    }
`;

const ProductsItems = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.Spacing8};

    ${breakpoint.up('1025px')} {
        gap: ${({ theme }) => theme.Spacing16};
    }
`;

const ProductItem = styled.div`
    position: relative;
`;

const SoldOutOverlay = styled.div<{ $isSoldOut: boolean }>`
    ${({ $isSoldOut }) =>
        $isSoldOut &&
        css`
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        opacity: 0.6;
        background-color: ${({ theme }) => theme.ColorBackgroundPrimary};
    `}
`;

const LoadingContainer = styled.div<{ $loading: boolean }>`
    position: relative;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.Spacing8};
    opacity: ${({ $loading }) => ($loading ? 0.6 : 1)};

    ${breakpoint.up('1025px')} {
        gap: ${({ theme }) => theme.Spacing16};
    }
`;

const StyledLoader = styled(Loader)`
    position: absolute;
    top: 25%;
    left: 50%;
    transform: translate(-50%, -50%);
    
    svg {
        color: ${({ theme }) => theme.ColorTextPrimary};
    }
`;

const Price = styled.div`
    display: grid;
    grid-template-areas:
        "title price"
        "itemCount savings";
`;

const PriceText = styled(Typography.Body)`
    grid-area: title;
`;

const ItemCount = styled(Typography.Body)`
    grid-area: itemCount;
`;

const StyledPriceModule = styled(PriceModule)`
    grid-area: price;
    justify-self: end;
`;

const PriceSavings = styled(Typography.Body)`
    grid-area: savings;
    justify-self: end;
    color: ${({ theme }) => theme.ColorTextSale};
`;

// Add or overwrite primary tag on current product
function addTagToCurrentProduct(
    products: SmartBundleViewModel['products'],
    currentProductId: number,
    splashText: string,
    tenant: 'Matas' | 'Kicks'
) {
    const isKicks = tenant === 'Kicks';

    return products.map((product) => {
        if (product.id === currentProductId) {
            return {
                ...product,
                splashes: {
                    ...product.splashes,
                    primary: {
                        placement: 'Primary',
                        displayType: 'Label',
                        type: 'Custom',
                        lines: [splashText],
                        backgroundColor: isKicks ? KicksColorBackgroundBrand : MatasColorBackgroundPrimary,
                        textColor: isKicks ? KicksColorTextPrimaryInverted : MatasColorTextPrimary,
                        isTransparant: false
                    }
                }
            } as SmartBundleViewModel['products'][number];
        }

        return product;
    });
}

function SmartBundle(props: SmartBundleProps) {
    const { productId, ...restProps } = props;
    const [data, setData] = useState<SmartBundleViewModel | undefined>();
    const [selectedProducts, setSelectedProducts] = useState<number[] | undefined>();
    const [deselectedProductIds, setDeselectedProductIds] = useState<number[]>([]);
    const { tenant } = useSegmentation();
    const { t } = useTranslation();

    const { isLoading, isFetching } = useGetSmartBundleQuery(
        {
            productId: productId,
            deselectedProductIds: deselectedProductIds?.length > 0 ? deselectedProductIds : undefined
        },
        {
            keepPreviousData: true,
            refetchOnWindowFocus: false,
            onSuccess: (data) => {
                if (data?.products) {
                    data.products = addTagToCurrentProduct(data.products, productId, t(CurrentProductSplash), tenant);
                }

                setData(data);

                if (data?.products && !selectedProducts) {
                    const initialSelectedProducts = data.products
                        .filter((product) => product.inStock)
                        .map((product) => product.id);
                    setSelectedProducts(initialSelectedProducts);
                }
            }
        }
    );

    const { mutateAsync: addToBasket } = useAddMultipleToBasketMutation({
        onSuccess: () => {
            updateBasket();

            toast.success<string>(
                t(selectedProducts?.length === 1 ? AddedToBasketMessage : AddedMultipleToBasketMessage),
                {
                    position: 'top-right'
                }
            );
        },
        onError: () => {
            toast.error<string>(t(GeneralExceptionError), {
                position: 'top-right'
            });
        }
    });

    function handleOnChange(productId: number) {
        if (selectedProducts?.includes(productId)) {
            const updatedSelectedProducts = selectedProducts.filter((id) => id !== productId);
            setSelectedProducts(updatedSelectedProducts);
            setDeselectedProductIds((prev) => [...prev, productId]);
        } else {
            setSelectedProducts((prev) => [...(prev || []), productId]);
            setDeselectedProductIds((prev) => prev.filter((id) => id !== productId));
        }
    }

    async function handleAddToCart() {
        await addToBasket({ params: { productIds: selectedProducts, addToBasketType: 'SmartBundle' } });

        GA4Tracking({
            eventName: 'add_to_cart',
            context: 'product_page',
            category: 'ecommerce',
            eventParams: [
                {
                    param_name: 'is_smart_bundle',
                    param_value: true
                },
                {
                    param_name: 'add_to_cart_quantity',
                    param_value: selectedProducts?.length || 0
                }
            ]
        });
    }

    if (!data) return null;

    return (
        <Container {...restProps}>
            <Typography.Body size="L" strong>
                {data?.headline}
            </Typography.Body>
            <Content>
                <ProductsItems>
                    {data?.products.map((product) => (
                        <ProductItem key={product.id}>
                            <HorizontalProductItem
                                key={product.id}
                                model={product}
                                showBorder={false}
                                actionModel={undefined}
                                favoriteModel={undefined}
                                checkboxModel={{
                                    value: product.id,
                                    checked: (selectedProducts?.includes(product.id) && product.inStock) || false,
                                    onChange: () => handleOnChange(product.id),
                                    disabled: !product.inStock
                                }}
                            />
                            <SoldOutOverlay $isSoldOut={!product.inStock} key={product.id} />
                        </ProductItem>
                    ))}
                </ProductsItems>
                <LoadingContainer $loading={isLoading || isFetching}>
                    <Price>
                        <PriceText size="L" strong>
                            {t(TotalText)}
                        </PriceText>
                        <ItemCount size="M">
                            {format(t(selectedProducts?.length === 1 ? SingleItem : MultipleItems))(
                                selectedProducts?.length
                            )}
                        </ItemCount>
                        {data?.priceInfo && (
                            <StyledPriceModule
                                price={data?.priceInfo.price}
                                priceBefore={data?.priceInfo.priceBefore}
                                isOnDiscount={data?.priceInfo.onDiscount}
                            />
                        )}
                        {data?.priceInfo.saveLabel && <PriceSavings size="M">{data?.priceInfo.saveLabel}</PriceSavings>}
                    </Price>

                    <Button
                        onClick={handleAddToCart}
                        disabled={selectedProducts?.length === 0 || !selectedProducts}
                        size="lg"
                    >
                        {selectedProducts && selectedProducts.length > 0
                            ? format(
                                  t(
                                      selectedProducts.length === 1
                                          ? AddToBasketButtonText
                                          : AddMultipleToBasketButtonText
                                  )
                              )(selectedProducts.length)
                            : t(DisabledAddToBasketButtonText)}
                    </Button>

                    {(isLoading || isFetching) && <StyledLoader size={18} />}
                </LoadingContainer>
            </Content>
        </Container>
    );
}

export default SmartBundle;
